import type { StudioEffect, StudioEffectType } from "@loumy/contracts";
import { WEBM_UNSUPPORTED_EFFECT_TYPES } from "@loumy/contracts";
import type { StudioWorkerOp } from "../browser-ops/protocol";

// §9.17 — PURE Logik des komponierbaren Effekt-Stacks (KEINE React-/Worker-/DOM-Abhängigkeit ⇒
// in vitest testbar). Hier liegen: Default-Parameter je Effekt, die Stack-Mutationen (add/remove/
// reorder/toggle/update) und die EINE Abbildung Effekt-Stack → StudioWorkerOp[] (Reihenfolge =
// Stack-Reihenfolge = Worker-Ausführungsreihenfolge). `chroma-key` ist der einzige Effekt, der ZWEI
// Worker-Ops stiftet (chroma_key + transparency_crop) und zugleich die Preis-`steps` beeinflusst
// (die Step-Ableitung lebt in studioPlan.deriveSteps; hier nur stackHasChromaKey als Signal).

export const EFFECT_LABELS: Record<StudioEffectType, string> = {
  speed: "Geschwindigkeit",
  color: "Farbe & Ton",
  transform: "Transform",
  "chroma-key": "Hintergrund entfernen",
  outline: "Kontur",
  glow: "Schein (Glow)",
  pixelate: "Pixelate (→ echte Pixel-Art)",
  "drop-shadow": "Schlagschatten",
};

/** Default-Parameter eines frisch hinzugefügten Effekts (im gültigen Schema-Bereich). */
export function defaultEffect(type: StudioEffectType, id: string): StudioEffect {
  switch (type) {
    case "speed":
      return { id, type, enabled: true, factor: 2 };
    case "color":
      return { id, type, enabled: true, brightness: 0, contrast: 0, saturation: 0, hue: 0, tint: null, tintStrength: 0 };
    case "transform":
      return { id, type, enabled: true, flipH: false, flipV: false, rotateQuarters: 0, scale: 100 };
    case "chroma-key":
      return { id, type, enabled: true, keyColor: "auto", tolerance: 20 };
    case "outline":
      return { id, type, enabled: true, thickness: 2, color: [0, 0, 0] };
    case "glow":
      return { id, type, enabled: true, radius: 6, color: [255, 255, 255], strength: 60 };
    case "pixelate":
      return { id, type, enabled: true, cell: 4, colors: 0, upscale: true };
    case "drop-shadow":
      return { id, type, enabled: true, dx: 4, dy: 4, blur: 3, color: [0, 0, 0], opacity: 50 };
  }
}

export function addEffect(stack: StudioEffect[], type: StudioEffectType, id: string): StudioEffect[] {
  return [...stack, defaultEffect(type, id)];
}

// Die Mutationen sind REFERENZ-STABIL: ändert sich nichts (id nicht im Stack / Wert schon gesetzt),
// kommt das GLEICHE Array zurück. So erkennt der Studio-Store (patchSelectedEffects → commit) den
// No-op und schreibt keinen leeren Undo-Schritt.

export function removeEffect(stack: StudioEffect[], id: string): StudioEffect[] {
  const next = stack.filter((e) => e.id !== id);
  return next.length === stack.length ? stack : next; // id nicht gefunden ⇒ No-op
}

export function toggleEffect(stack: StudioEffect[], id: string, enabled: boolean): StudioEffect[] {
  let changed = false;
  const next = stack.map((e) => {
    if (e.id !== id || e.enabled === enabled) return e;
    changed = true;
    return { ...e, enabled };
  });
  return changed ? next : stack;
}

/** Flach-gleich über die EIGENEN Schlüssel (primitive Felder; Array-Felder wie color/tint werden
 *  referenz-verglichen — ein Farb-Pick auf dieselben Werte trägt eine neue Array-Referenz und gilt
 *  daher als Änderung, was für die seltene „gleiche Farbe"-Geste akzeptabel ist). */
function effectShallowEqual(a: StudioEffect, b: StudioEffect): boolean {
  const ak = Object.keys(a) as (keyof StudioEffect)[];
  if (ak.length !== Object.keys(b).length) return false;
  return ak.every((k) => a[k] === b[k]);
}

/** Parameter-Patch in den passenden Effekt mergen — `id`/`type` bleiben unveränderlich. */
export function updateEffect(stack: StudioEffect[], id: string, patch: Record<string, unknown>): StudioEffect[] {
  let changed = false;
  const next = stack.map((e) => {
    if (e.id !== id) return e;
    const merged = { ...e, ...patch, id: e.id, type: e.type } as StudioEffect;
    if (effectShallowEqual(e, merged)) return e; // Patch verschiebt keinen Wert ⇒ No-op
    changed = true;
    return merged;
  });
  return changed ? next : stack;
}

/** Effekt im Stack verschieben (DnD-Umordnen); Indizes werden geklemmt. */
export function reorderEffect(stack: StudioEffect[], fromIndex: number, toIndex: number): StudioEffect[] {
  const n = stack.length;
  const a = Math.max(0, Math.min(Math.trunc(fromIndex), n - 1));
  const b = Math.max(0, Math.min(Math.trunc(toIndex), n - 1));
  if (a === b) return stack;
  const next = stack.slice();
  const [moved] = next.splice(a, 1);
  if (!moved) return stack;
  next.splice(b, 0, moved);
  return next;
}

/**
 * Effekt-Stack → Worker-Op-Liste eines CLIPS (NUR aktive Effekte), in Stack-Reihenfolge. `chroma-key`
 * stiftet NUR `chroma_key` (transparente Pixel); das `transparency_crop` läuft NICHT mehr pro Clip,
 * sondern GLOBAL nach dem Konkatenieren+Padden (Worker, `st-apply.cropTransparent`) — sonst croppte
 * jeder Clip seine eigene Union-BBox ⇒ Framing-Sprung an Clip-Grenzen (Codex-P1). Segment-Auswahl
 * und RIFE-Naht setzt der Aufrufer (buildClipGroups / st-apply.seam) DAVOR/DANACH.
 */
export function effectsToWorkerOps(stack: StudioEffect[]): StudioWorkerOp[] {
  const ops: StudioWorkerOp[] = [];
  for (const e of stack) {
    if (!e.enabled) continue;
    switch (e.type) {
      case "speed":
        ops.push({ op: "time_remap", factor: e.factor });
        break;
      case "color":
        ops.push({
          op: "color_adjust",
          brightness: e.brightness,
          contrast: e.contrast,
          saturation: e.saturation,
          hue: e.hue,
          tint: e.tint,
          tintStrength: e.tintStrength,
        });
        break;
      case "transform":
        ops.push({ op: "transform", flipH: e.flipH, flipV: e.flipV, rotateQuarters: e.rotateQuarters, scale: e.scale });
        break;
      case "chroma-key":
        ops.push({ op: "chroma_key", keyColor: e.keyColor, tolerance: e.tolerance });
        break;
      case "outline":
        ops.push({ op: "outline", thickness: e.thickness, color: e.color });
        break;
      case "glow":
        ops.push({ op: "glow", radius: e.radius, color: e.color, strength: e.strength });
        break;
      case "pixelate":
        ops.push({ op: "pixelate", cell: e.cell, colors: e.colors, upscale: e.upscale });
        break;
      case "drop-shadow":
        ops.push({ op: "drop_shadow", dx: e.dx, dy: e.dy, blur: e.blur, color: e.color, opacity: e.opacity });
        break;
    }
  }
  return ops;
}

/** Ist (mindestens) ein aktiver `chroma-key`-Effekt im Stack? (⇒ Preis-Steps chroma-key + crop). */
export function stackHasChromaKey(stack: StudioEffect[]): boolean {
  return stack.some((e) => e.enabled && e.type === "chroma-key");
}

type KeyFx = Extract<StudioEffect, { type: "chroma-key" }>;
/** ALLE aktiven chroma-key-Layer eines Clip-Stacks (die UI erlaubt mehrere). */
function activeKeys(stack: StudioEffect[]): KeyFx[] {
  return stack.filter((e): e is KeyFx => e.enabled && e.type === "chroma-key");
}

/**
 * Ist das Keyen über die Clips SERVER-reproduzierbar? Der WebM-Job (v1) wendet GENAU EINEN globalen
 * key auf die ganze Sequenz an. Reproduzierbar ist daher nur: GAR KEIN Key, ODER JEDER behaltene Clip
 * hat GENAU EINEN aktiven key, alle mit identischen Parametern (Farbe+Toleranz). Sperrend (Codex-P1):
 * mehrere Key-Layer auf EINEM Clip, „manche gekeyt/andere nicht", oder abweichende key-Parameter.
 */
export function webmKeyReproducible(perClip: StudioEffect[][]): boolean {
  const keysPerClip = perClip.map(activeKeys);
  if (keysPerClip.some((ks) => ks.length > 1)) return false; // ≥2 Keys/Clip ⇒ nicht durch EINEN globalen Key nachbildbar
  const withKey = keysPerClip.filter((ks) => ks.length === 1).map((ks) => ks[0]!);
  if (withKey.length === 0) return true; // kein Key → trivial reproduzierbar
  if (withKey.length !== perClip.length) return false; // manche Clips gekeyt, andere nicht
  const first = withKey[0]!;
  return withKey.every((k) => k.keyColor === first.keyColor && k.tolerance === first.tolerance);
}

/** Kann der WebM-Export-Job (v1) die Pro-Clip-Komposition server-IDENTISCH nachbilden? Der Job macht
 *  decode→cut→[chroma-key]→encode — also nur Schnitt + EIN GLOBALER chroma-key. Jeder andere aktive
 *  Effekt sperrt ihn; ebenso ein nicht global reproduzierbares Keyen (s. webmKeyReproducible). */
export function canExportWebm(perClip: StudioEffect[][]): boolean {
  if (perClip.flat().some((e) => e.enabled && WEBM_UNSUPPORTED_EFFECT_TYPES.has(e.type))) return false;
  return webmKeyReproducible(perClip);
}
